import {Navigate} from 'react-router-dom'
import {useAccess} from '@/hooks/useAccess'
import {getDefaultRoute} from '@/layout/helpers/layoutHelpers'
import {useMemo} from 'react'
import NoAccess from '@/components/common/NoAccess/NoAccess'

interface RoleBasedRedirectProps {
  children: React.ReactNode
}

/**
 * Component that redirects users to their first available route based on GrowthBook ACL.
 * Used as the index route to ensure users land on a page they have access to.
 * If the user's default route is '/', renders children (LandingPage).
 * If no routes are available, shows a "contact admin" message.
 * Otherwise, redirects to the appropriate route (e.g., /agile-metrics for DNAI users).
 */
const RoleBasedRedirect = ({children}: RoleBasedRedirectProps): React.ReactElement | null => {
  const {userRoles, acl} = useAccess()

  const defaultRoute = useMemo(() => {
    return getDefaultRoute(userRoles, acl)
  }, [userRoles, acl])

  // No roles assigned → show "contact admin" message
  if (userRoles.length === 0) {
    return <NoAccess />
  }

  // If the default route is '/', render children (LandingPage)
  // Otherwise, redirect to the first available route
  if (defaultRoute === '/') {
    return <>{children}</>
  }

  return <Navigate to={defaultRoute} replace />
}

export default RoleBasedRedirect
